import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { CheckCircle, ArrowRight, Phone, Leaf } from 'lucide-react';
import { TREATMENTS, TreatmentPageWrapper } from './TreatmentBase';
import { staggerContainer, staggerItem, scaleIn, fadeUp, viewportConfig } from '../../components/AnimationVariants';

export default function TreatmentPage() {
  const { slug } = useParams();
  const treatment = TREATMENTS.find(t => t.slug === slug);

  if (!treatment) return <Navigate to="/services" replace />;

  const Icon = treatment.icon;
  const idx = TREATMENTS.findIndex(t => t.slug === slug);
  const next = TREATMENTS[(idx + 1) % TREATMENTS.length];
  const prev = TREATMENTS[(idx - 1 + TREATMENTS.length) % TREATMENTS.length];

  return (
    <TreatmentPageWrapper treatment={treatment}>

      {/* Hero image banner */}
      <div className="relative rounded-3xl overflow-hidden mb-8 shadow-lg">
        <div className="relative h-72 md:h-96">
          <img
            src={treatment.heroPhoto || treatment.photo}
            alt={treatment.title}
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${treatment.color} opacity-70`} />
          <div className="absolute inset-0 p-8 md:p-12 flex items-end">
            <div className="flex items-center gap-5">
              <motion.div
                className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border border-white/30"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: 'spring', stiffness: 400 }}>
                <Icon className="w-8 h-8 text-white" />
              </motion.div>
              <div>
                <h2 className="font-serif text-white font-semibold text-3xl md:text-4xl leading-tight">{treatment.title}</h2>
                <p className="text-white/80 italic mt-1">{treatment.tagline}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Intro */}
      <motion.div className="bg-white rounded-3xl p-8 mb-6 shadow-sm border border-cream-deeper"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        <p className="text-gray-600 leading-relaxed text-base border-l-4 border-sage pl-5 italic">
          {treatment.intro}
        </p>
      </motion.div>

      {/* Sections — alternating image / text */}
      <div className="space-y-6 mb-6">
        {treatment.sections.map((section, i) => (
          <motion.div
            key={i}
            className="bg-white rounded-3xl overflow-hidden shadow-sm border border-cream-deeper"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07, duration: 0.5 }}
            viewport={{ once: true }}>
            <div className={`flex flex-col ${section.image ? (i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse') : ''}`}>
              {section.image && (
                <div className="md:w-2/5 flex-shrink-0">
                  <img
                    src={section.image}
                    alt={section.heading}
                    className="w-full h-56 md:h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="flex-1 p-7 md:p-8 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-6 rounded-full bg-sage" />
                  <h3 className="font-serif text-forest text-xl font-semibold">{section.heading}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm">{section.body}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* What's Included */}
      <motion.div
        className="bg-white rounded-3xl p-8 mb-6 shadow-sm border border-cream-deeper"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        <div className="flex items-center gap-2 mb-6">
          <Leaf className="w-5 h-5 text-sage" />
          <h3 className="font-serif text-forest font-semibold text-xl">What's Included</h3>
        </div>
        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
          {treatment.features.map((f, j) => (
            <motion.div key={j} variants={staggerItem}
              className="flex items-center gap-3 bg-sage-pale rounded-xl px-4 py-3"
              whileHover={{ scale: 1.02, x: 4 }}
              transition={{ type: 'spring', stiffness: 400 }}>
              <CheckCircle className="w-4 h-4 text-forest flex-shrink-0" />
              <span className="text-sm text-gray-700 font-medium">{f}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* CTA */}
      <motion.div className="bg-forest rounded-3xl p-8 mb-6 text-white text-center"
        variants={scaleIn} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        <h3 className="font-serif text-2xl font-semibold mb-2">Ready to get started?</h3>
        <p className="text-cream/75 text-sm mb-6 max-w-md mx-auto">
          Book a consultation and we'll create a personalised treatment plan around your needs.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/contact" className="btn-primary justify-center">
            Book a Consultation <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="tel:+442088665556" className="btn-outline-white justify-center flex items-center gap-2">
            <Phone className="w-4 h-4" /> 020 8866 5556
          </a>
        </div>
      </motion.div>

      {/* Prev / Next navigation */}
      <div className="grid grid-cols-2 gap-4">
        {/* Previous */}
        <Link to={`/services/${prev.slug}`}
          className="group relative rounded-2xl overflow-hidden border border-cream-deeper hover:border-forest hover:shadow-lg transition-all"
          style={{ minHeight: '120px' }}>
          <img src={prev.heroPhoto || prev.photo} alt={prev.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/90 via-forest-dark/50 to-transparent" />
          <div className="relative z-10 h-full flex flex-col justify-end p-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowRight className="w-3.5 h-3.5 text-gold rotate-180 flex-shrink-0" />
              <span className="text-xs text-gold/80 font-medium uppercase tracking-wide">Previous</span>
            </div>
            <div className="text-sm font-semibold text-white leading-tight">{prev.title}</div>
          </div>
        </Link>

        {/* Next */}
        <Link to={`/services/${next.slug}`}
          className="group relative rounded-2xl overflow-hidden border border-cream-deeper hover:border-forest hover:shadow-lg transition-all"
          style={{ minHeight: '120px' }}>
          <img src={next.heroPhoto || next.photo} alt={next.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/90 via-forest-dark/50 to-transparent" />
          <div className="relative z-10 h-full flex flex-col justify-end p-4 items-end text-right">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gold/80 font-medium uppercase tracking-wide">Next</span>
              <ArrowRight className="w-3.5 h-3.5 text-gold flex-shrink-0" />
            </div>
            <div className="text-sm font-semibold text-white leading-tight">{next.title}</div>
          </div>
        </Link>
      </div>

    </TreatmentPageWrapper>
  );
}
